import { ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  developers,
  instances as instancesApi,
  logs as logsApi,
  messages as messagesApi,
  usage as usageApi,
  webhooks as webhooksApi,
} from '../api/resources.js';
import {
  Banner,
  Card,
  Empty,
  ErrorState,
  Loading,
  Meter,
  Stat,
  StatusPill,
} from '../components/ui.js';
import { HighlightsCarousel } from '../components/highlights.js';
import { CHANGELOG, DOC_CARDS } from '../data/highlights.js';
import { formatDate, formatNumber, formatRelative, statusCodeTone } from '../lib/format.js';
import { useAsync, useTicker } from '../lib/hooks.js';

/**
 * Dashboard (PRD §29) and onboarding checklist (PRD §30).
 *
 * Answers the §120 questions at a glance: what is connected, what is failing, how much
 * has been consumed, and what to do next.
 */
export function OverviewPage() {
  useTicker();

  const overview = useAsync(async () => {
    const [instances, messages, logs, usage, webhooks, onboarding] = await Promise.all([
      instancesApi.list(),
      messagesApi.list({ limit: '200' }),
      logsApi.list(),
      usageApi.current(),
      webhooksApi.deliveries({ status: 'DEAD', limit: '50' }),
      developers.onboarding(),
    ]);
    return { instances, messages, logs, usage, webhooks, onboarding };
  }, []);

  if (overview.loading) return <Loading />;
  if (overview.error) return <ErrorState error={overview.error} onRetry={overview.reload} />;
  if (!overview.data) return null;

  const { instances, messages, logs, usage, webhooks, onboarding } = overview.data;

  const connected = instances.data.filter((instance) => instance.status === 'CONNECTED').length;
  const failing = instances.data.filter((instance) =>
    ['ERROR', 'BLOCKED', 'LOGGED_OUT'].includes(instance.status),
  ).length;

  const today = new Date().toISOString().slice(0, 10);
  const messagesToday = messages.data.filter((message) => message.createdAt.startsWith(today));
  const failedToday = messagesToday.filter((message) => message.status === 'FAILED').length;

  const requests = logs.data.length;
  const errors = logs.data.filter((log) => log.statusCode >= 400).length;
  const successRate = requests === 0 ? 100 : Math.round(((requests - errors) / requests) * 100);

  return (
    <>
      {onboarding.percent < 100 && (
        <Card
          title="Primeiros passos"
          description={`${onboarding.percent}% concluído`}
          actions={
            <span className="subtle">
              {onboarding.steps.filter((s) => s.done).length} de {onboarding.steps.length}
            </span>
          }
        >
          <Meter percent={onboarding.percent} />
          <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 0' }}>
            {onboarding.steps.map((step) => (
              <li key={step.key} className="row" style={{ padding: '4px 0' }}>
                <span style={{ color: step.done ? 'var(--ok)' : 'var(--text-subtle)' }}>
                  {step.done ? '✓' : '○'}
                </span>
                <span className={step.done ? 'muted' : undefined}>{step.label}</span>
                {step.key === onboarding.nextStep && (
                  <Link className="pill pill-brand" to={nextStepLink(step.key)}>
                    Continuar
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {failing > 0 && (
        <Banner tone="danger">
          <div>
            <strong>{failing} instância(s) precisam de atenção.</strong>{' '}
            <Link to="/instances">Ver instâncias</Link>
          </div>
        </Banner>
      )}

      <HighlightsCarousel />

      <div className="grid grid-4">
        <Stat
          label="Instâncias conectadas"
          value={`${connected}/${instances.data.length}`}
          hint={failing > 0 ? `${failing} com problema` : 'todas saudáveis'}
          {...(failing > 0 ? { tone: 'warn' as const } : {})}
        />
        <Stat
          label="Mensagens hoje"
          value={formatNumber(messagesToday.length)}
          hint={failedToday > 0 ? `${failedToday} falharam` : 'nenhuma falha'}
          {...(failedToday > 0 ? { tone: 'warn' as const } : {})}
        />
        <Stat
          label="Taxa de sucesso da API"
          value={`${successRate}%`}
          hint={`${errors} erro(s) nas últimas ${requests} chamadas`}
          {...(successRate < 95 ? { tone: 'danger' as const } : {})}
        />
        <Stat
          label="Webhooks na DLQ"
          value={formatNumber(webhooks.data.length)}
          hint={webhooks.data.length > 0 ? 'aguardando replay' : 'nada pendente'}
          {...(webhooks.data.length > 0 ? { tone: 'warn' as const } : {})}
        />
      </div>

      <div className="grid grid-2">
        <Card title="Consumo do período" description={`Plano ${usage.plan.name} · ${usage.period}`}>
          <div className="stack">
            {usage.metrics
              .filter((metric) => metric.limit !== null)
              .slice(0, 5)
              .map((metric) => (
                <div key={metric.metric}>
                  <div className="spread">
                    <span>{metric.metric}</span>
                    <span className="subtle">
                      {formatNumber(metric.used)} / {formatNumber(metric.limit ?? 0)}
                    </span>
                  </div>
                  <Meter percent={metric.percentUsed} />
                </div>
              ))}
          </div>
        </Card>

        <Card
          title="Instâncias"
          actions={
            <Link className="btn btn-sm" to="/instances">
              Ver todas
            </Link>
          }
          bodyless
        >
          {instances.data.length === 0 ? (
            <Empty title="Nenhuma instância ainda">
              <p className="muted">Crie a primeira para conectar um número.</p>
              <Link className="btn btn-primary btn-sm" to="/instances">
                Criar instância
              </Link>
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Última atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {instances.data.slice(0, 6).map((instance) => (
                    <tr key={instance.id}>
                      <td>
                        <Link to={`/instances/${instance.id}`}>{instance.name}</Link>
                      </td>
                      <td>
                        <StatusPill status={instance.status} />
                      </td>
                      <td className="subtle">{formatRelative(instance.lastSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Requisições recentes"
        description="Toda chamada tem um request id rastreável."
        actions={
          <Link className="btn btn-sm" to="/developers/logs">
            Log Explorer
          </Link>
        }
        bodyless
      >
        {logs.data.length === 0 ? (
          <Empty title="Nenhuma requisição registrada" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Horário</th>
                  <th>Método</th>
                  <th>Rota</th>
                  <th>Status</th>
                  <th>Duração</th>
                </tr>
              </thead>
              <tbody>
                {logs.data.slice(0, 8).map((log) => (
                  <tr key={log.requestId}>
                    <td className="subtle mono">
                      {new Date(log.createdAt).toLocaleTimeString('pt-BR')}
                    </td>
                    <td className="mono">{log.method}</td>
                    <td className="mono">{log.path}</td>
                    <td>
                      <span className={`pill pill-${statusCodeTone(log.statusCode)}`}>
                        {log.statusCode}
                      </span>
                    </td>
                    <td className="subtle">{log.durationMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card
          title="Documentação"
          description="Os caminhos mais pedidos, sem sair do console."
          actions={
            <Link className="btn btn-sm" to="/developers/explorer">
              API Explorer
            </Link>
          }
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {DOC_CARDS.map((doc) => (
              <Link
                key={doc.key}
                to={doc.to}
                className="group flex items-start gap-3 rounded-md border border-border p-3 text-foreground no-underline transition-colors hover:border-primary hover:bg-primary-soft/40 hover:no-underline"
              >
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground group-hover:bg-primary-soft group-hover:text-primary [&_svg]:size-4">
                  <BookOpen />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{doc.title}</span>
                  <span className="block text-xs text-muted-foreground">{doc.body}</span>
                </span>
                <ArrowRight
                  className="ml-auto size-4 shrink-0 self-center text-subtle group-hover:text-primary"
                  aria-hidden="true"
                />
              </Link>
            ))}
          </div>
        </Card>

        <Card title="Novidades" description="O que mudou na plataforma." bodyless>
          <ol className="m-0 list-none divide-y divide-border p-0">
            {CHANGELOG.slice(0, 5).map((entry) => (
              <li key={`${entry.date}-${entry.title}`} className="flex gap-3 px-4 py-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary [&_svg]:size-3.5">
                  <Sparkles />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] text-subtle">{formatDate(entry.date)}</span>
                  <span className="block text-sm font-medium">
                    {entry.to ? <Link to={entry.to}>{entry.title}</Link> : entry.title}
                  </span>
                  <span className="block text-xs text-muted-foreground">{entry.body}</span>
                </span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </>
  );
}

function nextStepLink(key: string): string {
  if (key.startsWith('instance')) return '/instances';
  if (key.startsWith('api_key')) return '/developers/keys';
  if (key.startsWith('webhook')) return '/webhooks';
  if (key === 'first_message') return '/messages';
  return '/';
}
